import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { ExpandCircleDownOutlined } from '@mui/icons-material'

import Table from 'components/Table'

import Resource from 'types/Resource'
import { longMonthYear, removeScriptTags } from 'helpers/formatters'
import { screenSize } from 'styles/shared'
import GA4 from 'helpers/ga4'

type Props = {
  resource: Resource
}

const ThermostatReleaseRow = ({ resource }: Props) => {
  const [isCaretCollapsed, setIsCaretCollapsed] = useState(true)

  const handleCaretClick = () => {
    if (isCaretCollapsed) {
      GA4.sendEvent({
        action: 'Expand Stat Release Notes',
        category: 'Resources',
        label: `Release Version: ${resource.version} for Model(s) ${resource.models?.join(', ')}`
      })
    }

    setIsCaretCollapsed(!isCaretCollapsed)
  }

  if (resource == null) {
    return <></>
  }

  return (
    <S.TableRow key={resource.id}>
      <S.Version>
        {resource.version}
      </S.Version>
      <S.ModelCell>
        {resource.models && resource.models.join(', ')}
      </S.ModelCell>
      <S.DateCell>
        { longMonthYear(resource.releaseDate)}
      </S.DateCell>
      <S.DescriptionCell>
        <S.CaretIcon
          onClick={handleCaretClick}
          aria-label={`${isCaretCollapsed ? 'Collapse' : 'Expand'}`}
          isCaretCollapsed={isCaretCollapsed}
        />
        <S.Description isCaretCollapsed={isCaretCollapsed}
          dangerouslySetInnerHTML={{ __html: removeScriptTags(resource.description) }}
        />
      </S.DescriptionCell>
    </S.TableRow>
  )
}

export default ThermostatReleaseRow

const cellStyle = css`
  padding: 15px;
  min-width: 120px;
  align-items: flex-start;

  @media ${screenSize.medium} {
    font-size: 12px;
  }

  @media ${screenSize.small} {
    padding: 8px 15px;
  }
`

const S = {
  DescriptionCell: styled(Table.Cell)`
    ${cellStyle}
    flex-basis: 50%;
    
  `,
  Version: styled(Table.Cell)`
    ${cellStyle}
    flex-basis: 15%;
    padding-left: 25px;

    @media ${screenSize.medium} {
      padding-left: 2rem;
    }
  `,
  DateCell: styled(Table.Cell)`
    ${cellStyle}
    flex-basis: 20%;
  `,
  ModelCell: styled(Table.Cell)`
    ${cellStyle}
    flex-basis: 15%;
  `,
  TableRow: styled(Table.Row)`
    padding-top: 10px;
    align-items: flex-start;

    @media ${screenSize.small} {
      align-items: unset;
    }
  `,
  Description: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isCaretCollapsed'].includes(prop)
  }) <{ isCaretCollapsed: boolean }>`
    overflow: hidden;
    text-overflow: ${({ isCaretCollapsed }) => (isCaretCollapsed ? 'ellipsis' : 'unset')};
    width: 600px;
    margin-right: 10px;
    white-space: ${({ isCaretCollapsed }) => (isCaretCollapsed ? 'nowrap' : 'normal')};
    height: ${({ isCaretCollapsed }) => (isCaretCollapsed ? '20px' : 'auto')};

    @media ${screenSize.mediumOnly} {
      width: 25vw;
    }
  `,
  CaretIcon: styled(ExpandCircleDownOutlined).withConfig({
    shouldForwardProp: (prop) => !['isCaretCollapsed'].includes(prop)
  }) <{ isCaretCollapsed: boolean }>`
    margin-right: 10px;
    transform: ${({ isCaretCollapsed }) => (isCaretCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)')};
  `
}
